import { useCallback, useEffect, useState } from 'react'
import type { TFunction } from 'i18next'
import {
  useCaseScope,
  useNoteMutations,
  useScopeMutations,
  useScopeNotes,
  useScopeVersions,
} from '../../../hooks'
import type { CaseScopeNote, ScopeNoteTag } from '../../../types/case-container'

export interface CaseScopeTabState {
  scope: ReturnType<typeof useCaseScope>['scope']
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  versions: ReturnType<typeof useScopeVersions>['versions']
  loadingVersions: boolean
  notes: ReturnType<typeof useScopeNotes>['notes']
  notesTotal: number
  loadingNotes: boolean
  saving: boolean
  savingNote: boolean
  isEditing: boolean
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>
  editContent: string
  setEditContent: React.Dispatch<React.SetStateAction<string>>
  changeSummary: string
  setChangeSummary: React.Dispatch<React.SetStateAction<string>>
  showHistory: boolean
  setShowHistory: React.Dispatch<React.SetStateAction<boolean>>
  showNoteForm: boolean
  setShowNoteForm: React.Dispatch<React.SetStateAction<boolean>>
  noteContent: string
  setNoteContent: React.Dispatch<React.SetStateAction<string>>
  noteTags: ScopeNoteTag[]
  editingNoteId: string | null
  setEditingNoteId: React.Dispatch<React.SetStateAction<string | null>>
  handleSave: () => Promise<void>
  handleCancel: () => void
  handleCreateNote: () => Promise<void>
  handleUpdateNote: () => Promise<void>
  handleDeleteNote: (noteId: string) => Promise<void>
  startEditNote: (note: CaseScopeNote) => void
  toggleTag: (tag: ScopeNoteTag) => void
  resetNoteForm: () => void
}

interface NoteFormState {
  noteContent: string
  setNoteContent: React.Dispatch<React.SetStateAction<string>>
  noteTags: ScopeNoteTag[]
  setNoteTags: React.Dispatch<React.SetStateAction<ScopeNoteTag[]>>
  editingNoteId: string | null
  setEditingNoteId: React.Dispatch<React.SetStateAction<string | null>>
  showNoteForm: boolean
  setShowNoteForm: React.Dispatch<React.SetStateAction<boolean>>
  toggleTag: (tag: ScopeNoteTag) => void
  resetNoteForm: () => void
}

interface ScopeEditState {
  isEditing: boolean
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>
  editContent: string
  setEditContent: React.Dispatch<React.SetStateAction<string>>
  changeSummary: string
  setChangeSummary: React.Dispatch<React.SetStateAction<string>>
  showHistory: boolean
  setShowHistory: React.Dispatch<React.SetStateAction<boolean>>
}

function useScopeEditState(): ScopeEditState {
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState('')
  const [changeSummary, setChangeSummary] = useState('')
  const [showHistory, setShowHistory] = useState(false)
  return {
    isEditing,
    setIsEditing,
    editContent,
    setEditContent,
    changeSummary,
    setChangeSummary,
    showHistory,
    setShowHistory,
  }
}

function useNoteFormState(): NoteFormState {
  const [showNoteForm, setShowNoteForm] = useState(false)
  const [noteContent, setNoteContent] = useState('')
  const [noteTags, setNoteTags] = useState<ScopeNoteTag[]>([])
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)

  const toggleTag = useCallback((tag: ScopeNoteTag): void => {
    setNoteTags((prev) =>
      prev.includes(tag) ? prev.filter((existing) => existing !== tag) : [...prev, tag]
    )
  }, [])

  const resetNoteForm = useCallback((): void => {
    setShowNoteForm(false)
    setEditingNoteId(null)
    setNoteContent('')
    setNoteTags([])
  }, [])

  return {
    noteContent,
    setNoteContent,
    noteTags,
    setNoteTags,
    editingNoteId,
    setEditingNoteId,
    showNoteForm,
    setShowNoteForm,
    toggleTag,
    resetNoteForm,
  }
}

interface ScopeMutationArgs {
  caseId: string
  editContent: string
  changeSummary: string
  updateScope: ReturnType<typeof useScopeMutations>['update']
  refetch: () => Promise<void>
  refetchVersions: () => Promise<void>
  scope: ReturnType<typeof useCaseScope>['scope']
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>
  setChangeSummary: React.Dispatch<React.SetStateAction<string>>
  setEditContent: React.Dispatch<React.SetStateAction<string>>
}

function useScopeMutationHandlers(args: ScopeMutationArgs): {
  handleSave: () => Promise<void>
  handleCancel: () => void
} {
  const {
    caseId,
    editContent,
    changeSummary,
    updateScope,
    refetch,
    refetchVersions,
    scope,
    setIsEditing,
    setChangeSummary,
    setEditContent,
  } = args

  const handleSave = useCallback(async () => {
    if (!caseId) return
    const result = await updateScope(caseId, {
      content: editContent,
      ...(changeSummary ? { changeSummary } : {}),
    })
    if (result) {
      setIsEditing(false)
      setChangeSummary('')
      void refetch()
      void refetchVersions()
    }
  }, [
    caseId,
    editContent,
    changeSummary,
    updateScope,
    refetch,
    refetchVersions,
    setIsEditing,
    setChangeSummary,
  ])

  const handleCancel = useCallback((): void => {
    setEditContent(scope?.content ?? '')
    setChangeSummary('')
    setIsEditing(false)
  }, [scope?.content, setEditContent, setChangeSummary, setIsEditing])

  return { handleSave, handleCancel }
}

interface NoteMutationArgs {
  caseId: string
  noteForm: NoteFormState
  createNote: ReturnType<typeof useNoteMutations>['create']
  updateNoteApi: ReturnType<typeof useNoteMutations>['update']
  deleteNote: ReturnType<typeof useNoteMutations>['remove']
  refetchNotes: () => Promise<void>
  t: TFunction<'cases'>
}

function useNoteMutationHandlers(args: NoteMutationArgs): {
  handleCreateNote: () => Promise<void>
  handleUpdateNote: () => Promise<void>
  handleDeleteNote: (noteId: string) => Promise<void>
  startEditNote: (note: CaseScopeNote) => void
} {
  const { caseId, noteForm, createNote, updateNoteApi, deleteNote, refetchNotes, t } = args

  const handleCreateNote = useCallback(async (): Promise<void> => {
    if (!noteForm.noteContent.trim()) return
    const result = await createNote(caseId, {
      content: noteForm.noteContent,
      tags: noteForm.noteTags,
    })
    if (result) {
      noteForm.resetNoteForm()
      void refetchNotes()
    }
  }, [caseId, noteForm, createNote, refetchNotes])

  const handleUpdateNote = useCallback(async (): Promise<void> => {
    if (!noteForm.editingNoteId || !noteForm.noteContent.trim()) return
    const result = await updateNoteApi(caseId, noteForm.editingNoteId, {
      content: noteForm.noteContent,
      tags: noteForm.noteTags,
    })
    if (result) {
      noteForm.resetNoteForm()
      void refetchNotes()
    }
  }, [caseId, noteForm, updateNoteApi, refetchNotes])

  const handleDeleteNote = useCallback(
    async (noteId: string): Promise<void> => {
      if (window.confirm(t('scope.confirmDeleteNote'))) {
        const success = await deleteNote(caseId, noteId)
        if (success) void refetchNotes()
      }
    },
    [caseId, deleteNote, refetchNotes, t]
  )

  const startEditNote = useCallback(
    (note: CaseScopeNote): void => {
      noteForm.setEditingNoteId(note.id)
      noteForm.setNoteContent(note.content)
      noteForm.setNoteTags(note.tags as ScopeNoteTag[])
      noteForm.setShowNoteForm(true)
    },
    [noteForm]
  )

  return { handleCreateNote, handleUpdateNote, handleDeleteNote, startEditNote }
}

export function useCaseScopeTab(args: {
  caseId: string
  t: TFunction<'cases'>
}): CaseScopeTabState {
  const { caseId, t } = args
  const { scope, loading, error, refetch } = useCaseScope(caseId)
  const { versions, loading: loadingVersions, refetch: refetchVersions } = useScopeVersions(caseId)
  const {
    notes,
    total: notesTotal,
    loading: loadingNotes,
    refetch: refetchNotes,
  } = useScopeNotes(caseId)
  const { saving, update: updateScope } = useScopeMutations()
  const { saving: savingNote, create: createNote, update: updateNoteApi, remove: deleteNote } = useNoteMutations()

  const edit = useScopeEditState()
  const { isEditing, setIsEditing, editContent, setEditContent, changeSummary, setChangeSummary, showHistory, setShowHistory } = edit
  const noteForm = useNoteFormState()

  useEffect(() => {
    if (scope?.content) setEditContent(scope.content)
  }, [scope?.content, setEditContent])

  const { handleSave, handleCancel } = useScopeMutationHandlers({
    caseId,
    editContent,
    changeSummary,
    updateScope,
    refetch,
    refetchVersions,
    scope,
    setIsEditing,
    setChangeSummary,
    setEditContent,
  })

  const { handleCreateNote, handleUpdateNote, handleDeleteNote, startEditNote } =
    useNoteMutationHandlers({ caseId, noteForm, createNote, updateNoteApi, deleteNote, refetchNotes, t })

  return {
    scope,
    loading,
    error,
    refetch,
    versions,
    loadingVersions,
    notes,
    notesTotal,
    loadingNotes,
    saving,
    savingNote,
    isEditing,
    setIsEditing,
    editContent,
    setEditContent,
    changeSummary,
    setChangeSummary,
    showHistory,
    setShowHistory,
    showNoteForm: noteForm.showNoteForm,
    setShowNoteForm: noteForm.setShowNoteForm,
    noteContent: noteForm.noteContent,
    setNoteContent: noteForm.setNoteContent,
    noteTags: noteForm.noteTags,
    editingNoteId: noteForm.editingNoteId,
    setEditingNoteId: noteForm.setEditingNoteId,
    handleSave,
    handleCancel,
    handleCreateNote,
    handleUpdateNote,
    handleDeleteNote,
    startEditNote,
    toggleTag: noteForm.toggleTag,
    resetNoteForm: noteForm.resetNoteForm,
  }
}
